import { type ComponentProps, useState } from 'react';
import Select, { type SingleValue } from 'react-select';
import { CustomLabel } from '@/components/typography';
import { cn } from '@/lib/utils';
import './dropdown.scss';

/** Above block-menu popover (9999) and ref-chip hints (10002). */
const MenuPortalZIndex = 10010;

export type DropdownOptionProps = {
  value: string;
  label: string;
};

type DropdownOwnProps = {
  options: DropdownOptionProps[];
  value?: DropdownOptionProps;
  label?: string;
  onChange?: (option: DropdownOptionProps) => void;
  defaultValue?: string;
  disabled?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

type DropdownProps = Omit<ComponentProps<'div'>, 'onChange' | 'defaultValue'> &
  DropdownOwnProps;

function Dropdown({
  className,
  options,
  value,
  label,
  onChange,
  defaultValue,
  disabled,
  onMenuOpen,
  onMenuClose,
  ...props
}: DropdownProps) {
  const inheritDefaultOption = options.find(n => n.value === defaultValue);
  const defaultOption =
    inheritDefaultOption ?? options.at(0) ?? (value as DropdownOptionProps);
  const [internalSelected, setInternalSelected] =
    useState<DropdownOptionProps>(defaultOption);
  const [menuPortalTarget] = useState<HTMLElement | undefined>(() =>
    typeof document !== 'undefined' ? document.body : undefined,
  );

  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : internalSelected;

  const handleOnChange = (val: SingleValue<DropdownOptionProps>) => {
    if (val === null) return;
    onChange?.(val);
    if (!isControlled) setInternalSelected(val);
  };

  return (
    <div className={cn('dropdown', className)} {...props}>
      {label && <CustomLabel>{label}</CustomLabel>}
      <Select
        onChange={handleOnChange}
        value={displayValue}
        options={options}
        className="react-select-container"
        classNamePrefix="react-select"
        isDisabled={disabled}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        styles={{
          menuPortal: base => ({ ...base, zIndex: MenuPortalZIndex }),
        }}
      />
    </div>
  );
}

export { Dropdown };
